/**
 * CompetitionBlock — Renders a competition entity reference block
 *
 * Fetches competition data by ID and renders a card with name, description,
 * and link to the competition page.
 * Display modes: Card (default), Hero, Compact.
 */

import { useNavigate } from "react-router";
import { useGetCompetition } from "@/hooks/useGetCompetition";

interface CompetitionBlockProps {
    competitionId?: string;
    displayMode?: string;
    sectionTitle?: string;
}

export function CompetitionBlock({ competitionId, displayMode = "Card", sectionTitle }: CompetitionBlockProps) {
    const navigate = useNavigate();
    const { data: competition, isLoading } = useGetCompetition(competitionId);

    const mode = displayMode?.toLowerCase() || "card";

    if (!competitionId || isLoading) {
        return isLoading ? <div className="entity-block entity-block--loading" style={{ minHeight: 120, background: "var(--hub-card-bg, rgba(255,255,255,0.05))", borderRadius: 12, animation: "pulse 1.5s infinite" }} /> : null;
    }

    if (!competition) return null;

    const isHero = mode === "hero";
    const isCompact = mode === "compact";

    return (
        <div className={`entity-block entity-block--competition entity-block--${mode}`}>
            {sectionTitle && <h3 className="block-section-title" style={{ color: "var(--hub-text-primary, #fff)", marginBottom: "0.75rem" }}>{sectionTitle}</h3>}
            <div
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/competitions/${competitionId}`)}
                onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault(); navigate(`/competitions/${competitionId}`);
                    }
                }}
                style={{
                    background: "var(--hub-card-bg, rgba(255,255,255,0.08))",
                    borderRadius: 12,
                    padding: isCompact ? "1rem" : isHero ? "2rem" : "1.5rem",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    border: "1px solid var(--hub-border-subtle, rgba(255,255,255,0.1))",
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.3)";
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "";
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: isCompact ? 0 : "0.5rem" }}>
                    <span style={{ background: "var(--hub-primary, #777777)", color: "#fff", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Competition</span>
                    {isCompact && <span style={{ color: "var(--hub-text-primary, #fff)", fontWeight: 600, fontSize: 15 }}>{competition.name}</span>}
                </div>
                {!isCompact &&
                    <>
                        <h4 style={{ color: "var(--hub-text-primary, #fff)", fontSize: isHero ? 24 : 18, fontWeight: 700, margin: "0.5rem 0 0.25rem" }}>{competition.name}</h4>
                        {competition.description &&
                            <p style={{ color: "var(--hub-text-secondary, rgba(255,255,255,0.7))", fontSize: 14, margin: 0, lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{competition.description}</p>
                        }
                    </>
                }
            </div>
        </div>
    );
}
